import * as React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Item, type FsEntryPermissionRole } from "@/types/fse";
import { useToastDefault } from "@/hooks/use-toast-default";
import { CircleLoader } from "@/components/ui/circleLoader";
import { ShareModalUserFormSelectUsers } from "./formSelectUsers";
import { Userdata } from "@/types/user";
import { useCreatePermissionMutation } from "@/api/files";

interface ShareModalUserFormProps {
  item: Item;
  setIsNewUserSelected: (isNewUserSelected: boolean) => void;
  isNewUserSelected: boolean;
  isReadOnly: boolean;
}

interface FormInputs {
  users: Userdata[];
  role: FsEntryPermissionRole;
}

export const ShareModalUserForm: React.FC<ShareModalUserFormProps> = ({
  item,
  isNewUserSelected,
  isReadOnly,
  setIsNewUserSelected,
}) => {
  const methods = useForm<FormInputs>({
    defaultValues: {
      users: [],
    },
  });

  const { showToast } = useToastDefault();

  const users = useWatch({
    control: methods.control,
    name: "users",
  });

  React.useEffect(() => {
    if (!users.length) {
      setIsNewUserSelected(false);
    } else {
      setIsNewUserSelected(true);
    }
  }, [users, setIsNewUserSelected]);

  const [createPermission] = useCreatePermissionMutation();

  const onSubmit = async (data: FormInputs) => {
    await createPermission({
      fse_id: item.id,
      role: data.role,
      users: data.users.map(user => user.id),
    }).unwrap();
    showToast("Permissions updated", "Permissions has been created successfully.");
    methods.resetField("users");
    setIsNewUserSelected(false);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex">
          <div className="w-5/6">
            <ShareModalUserFormSelectUsers isReadOnly={isReadOnly} />
          </div>
          <div className="w-1/6">
            <select
              disabled={isReadOnly}
              style={{
                lineHeight: "26px",
              }}
              {...methods.register("role")}
              className="ml-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
            >
              <option value="owner">Owner</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
        </div>
        {isNewUserSelected && (
          <DialogFooter className="mt-3">
            <Button variant="default" type="submit">
              {methods.formState.isSubmitting && <CircleLoader />}
              {methods.formState.isSubmitting ? "Saving" : "Share"}
            </Button>
          </DialogFooter>
        )}
      </form>
    </FormProvider>
  );
};
